import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import {
  add,
  sub,
  format,
  isToday,
  isSameDay,
  parseISO,
  startOfMonth,
  startOfWeek,
  endOfWeek,
  startOfDay,
  eachDayOfInterval,
  getDay,
} from 'date-fns'
import { useNavigate } from 'react-router-dom'
import Button from '@/v2/components/common/Button'
import LeftIcon from '@mui/icons-material/ChevronLeft'
import RightIcon from '@mui/icons-material/ChevronRight'
import IconButton from '@/v2/components/common/IconButton'
import Select from '@/v2/components/common/Select'
import SelectMui from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import ListSubheader from '@mui/material/ListSubheader'
import MultipleSelect from '@/v2/components/common/MultipleSelect'
import { DATE_PARAM_FORMAT } from '@/v2/constants'
import { nowOnClinicTimezone, safeFormat } from '@/v2/utils/convert'
import useDetectDevice from '@/v2/hooks/useDetectDevice'
import {
  Wrapper,
  NavigationWrapper,
  DateWrapper,
  FilterWrapper,
  Filters,
  ButtonsWrapper,
  MobileWrapper,
  MobileHeaderRow,
  MobileDateSelector,
  MobileDateText,
  MobileTodayButton,
  MobileWeekStrip,
  MobileWeekDay,
  MobileWeekDayLabel,
  MobileWeekDayNumber,
  MobileFiltersRow,
} from './styles'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { useQuery } from '@tanstack/react-query'
import { baseURL, headers } from '@/v2/constants/requests'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useGetDoctors } from '@/v2/hooks/useDoctors'

const today = format(nowOnClinicTimezone(), 'yyyy-MM-dd')

const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const fetchRooms = async () => {
  const res = await fetch(`${baseURL}/v2/rest/rooms`, {
    method: 'GET',
    headers,
  })
  if (!res.ok) {
    throw new Error(`Error fetching rooms: ${res.statusText}`)
  }
  return res.json()
}

// Sentinel values for the mobile chair selector's "show every chair" entries.
const ALL_ROOMS = '__all_rooms__'
const ALL_DOCTORS = '__all_doctors__'

const Filterbar = ({
  onChangeSubject,
  onChangeType,
  subjectView,
  selectedColumn,
  onChangeColumn,
}) => {
  const navigate = useNavigate()
  const { isMobile } = useDetectDevice()
  const [selectedType, setSelectedType] = useState('')

  // Fetch rooms and doctors for the mobile column selector.
  const { data: rooms = [] } = useQuery({
    queryKey: ['rooms'],
    queryFn: fetchRooms,
    enabled: isMobile,
  })
  const { data: doctors = [] } = useGetDoctors()

  const { data: appointmentTypesData, isLoading, isError } = useQuery({
    queryKey: ['appointmentTypes'],
    queryFn: async () => {
      const res = await fetch(`${baseURL}/v2/rest/appointment_types`, {
        method: 'GET',
        headers,
      })
      if (!res.ok) {
        throw new Error(`Error fetching appointment types: ${res.statusText}`)
      }
      return res.json()
    },
  })

  const typesArray = appointmentTypesData
    ? [
        ...(appointmentTypesData.onSite || []),
        ...(appointmentTypesData.virtual || []),
      ]
    : []

  const appointmentTypes =
    isLoading || isError
      ? []
      : typesArray
          .map(({ id, title, color }) => ({ value: id, label: title, color }))
          .sort((a, b) => (a.label > b.label ? 1 : -1))

  const theme = useTheme()
  const { view = 'day', date = today } = useParams()
  const currentDate = parseISO(date)
  const todayDate = nowOnClinicTimezone()
  const dateFormat = {
    day: 'MMMM dd, yyyy',
    weekStart: 'MMMM dd',
    weekEnd: 'dd, yyyy',
    month: 'MMMM yyyy',
  }
  const firstDay = {
    day: currentDate,
    week: startOfWeek(currentDate),
    month: startOfMonth(currentDate),
  }

  // Get week days for the mobile strip
  const weekStart = startOfWeek(currentDate)
  const weekEnd = endOfWeek(currentDate)
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd })

  const handleDayClick = day => {
    navigate(`/v2/calendar/day/${format(day, DATE_PARAM_FORMAT)}`)
  }

  const handleTodayClick = () => {
    navigate(`/v2/calendar/day/${format(todayDate, DATE_PARAM_FORMAT)}`)
  }

  // Build the mobile column options. The day shows every chair by default ("All
  // rooms"/"All doctors"); picking a specific room or doctor narrows to just it.
  const { roomOptions, doctorOptions } = React.useMemo(() => {
    if (!isMobile) return { roomOptions: [], doctorOptions: [] }
    return {
      roomOptions: rooms.map(({ id, title }) => ({
        value: `room-${id}`,
        label: title,
      })),
      doctorOptions: (doctors || []).map(({ id, lastName }) => ({
        value: `doctor-${id}`,
        label: `Dr. ${lastName}`,
      })),
    }
  }, [isMobile, rooms, doctors])

  // Update the selected column and the subject view it implies. The "all" entries
  // clear the specific-chair filter so every chair of that view shows.
  const handleColumnChange = value => {
    if (value === ALL_ROOMS) {
      onChangeColumn(null)
      onChangeSubject('room')
    } else if (value === ALL_DOCTORS) {
      onChangeColumn(null)
      onChangeSubject('doctor')
    } else {
      onChangeColumn(value)
      onChangeSubject(value.startsWith('doctor-') ? 'doctor' : 'room')
    }
  }

  // With no specific chair picked, show the "all" entry for the current view.
  const getSelectValue = () =>
    selectedColumn || (subjectView === 'doctor' ? ALL_DOCTORS : ALL_ROOMS)

  if (isMobile) {
    return (
      <MobileWrapper theme={theme.palette}>
        <MobileHeaderRow>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <MobileDatePicker
              value={currentDate}
              onChange={newDate => {
                if (newDate) {
                  navigate(
                    `/v2/calendar/day/${format(newDate, DATE_PARAM_FORMAT)}`
                  )
                }
              }}
              renderInput={({ inputRef, inputProps, InputProps }) => (
                <MobileDateSelector ref={inputRef} onClick={inputProps.onClick}>
                  <MobileDateText>
                    {safeFormat(currentDate, 'MMMM d, yyyy')}
                  </MobileDateText>
                  <KeyboardArrowDownIcon />
                </MobileDateSelector>
              )}
            />
          </LocalizationProvider>
          <MobileTodayButton onClick={handleTodayClick}>
            Today
          </MobileTodayButton>
        </MobileHeaderRow>
        <MobileWeekStrip>
          {weekDays.map((day, index) => (
            <MobileWeekDay key={index} onClick={() => handleDayClick(day)}>
              <MobileWeekDayLabel>{WEEK_DAYS[getDay(day)]}</MobileWeekDayLabel>
              <MobileWeekDayNumber
                $isSelected={isSameDay(day, currentDate)}
                $isToday={isToday(day)}
              >
                {format(day, 'd')}
              </MobileWeekDayNumber>
            </MobileWeekDay>
          ))}
        </MobileWeekStrip>
        <MobileFiltersRow>
          <SelectMui
            displayEmpty
            value={getSelectValue()}
            onChange={e => handleColumnChange(e.target.value)}
            sx={{ flex: 1 }}
          >
            {roomOptions.length > 0 && (
              <ListSubheader
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  lineHeight: '32px',
                }}
              >
                Rooms
              </ListSubheader>
            )}
            {roomOptions.length > 0 && (
              <MenuItem value={ALL_ROOMS}>All rooms</MenuItem>
            )}
            {roomOptions.map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
            {doctorOptions.length > 0 && (
              <ListSubheader
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  lineHeight: '32px',
                }}
              >
                Doctors
              </ListSubheader>
            )}
            {doctorOptions.length > 0 && (
              <MenuItem value={ALL_DOCTORS}>All doctors</MenuItem>
            )}
            {doctorOptions.map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </SelectMui>
          <Select
            key="filter-type"
            value={selectedType}
            options={[
              { value: '', label: 'All appointments' },
              ...appointmentTypes,
            ]}
            onChange={type => {
              setSelectedType(type)
              onChangeType(type ? [type] : [])
            }}
          />
        </MobileFiltersRow>
      </MobileWrapper>
    )
  }

  return (
    <Wrapper theme={theme.palette}>
      <NavigationWrapper>
        <IconButton
          title="Previous"
          hovercolor="black"
          aria-label="Previous"
          href={`/v2/calendar/${view}/${safeFormat(
            sub(firstDay[view], { [`${view}s`]: 1 }),
            DATE_PARAM_FORMAT
          )}`}
        >
          <LeftIcon />
        </IconButton>
        <IconButton
          title="Next"
          hovercolor="black"
          aria-label="Next day/week/month"
          href={`/v2/calendar/${view}/${safeFormat(
            add(firstDay[view], { [`${view}s`]: 1 }),
            DATE_PARAM_FORMAT
          )}`}
        >
          <RightIcon />
        </IconButton>
        <DateWrapper bold variant="h1">
          {view === 'week'
            ? `${safeFormat(firstDay.week, dateFormat.weekStart)}-${safeFormat(
                endOfWeek(firstDay.week),
                dateFormat.weekEnd
              )}`
            : safeFormat(currentDate, dateFormat[view])}
        </DateWrapper>
      </NavigationWrapper>
      <FilterWrapper>
        {view !== 'month' ? (
          <Filters>
            <Select
              key="view"
              defaultValue="room"
              options={[
                { value: 'room', label: 'Rooms' },
                { value: 'doctor', label: 'Doctors' },
              ]}
              onChange={onChangeSubject}
            />
            <MultipleSelect
              chipOptions
              key="filter-type"
              placeholder="Filter by appointment"
              options={appointmentTypes}
              onChange={types => onChangeType(types)}
            />
          </Filters>
        ) : null}
        <ButtonsWrapper>
          <Button
            key="today"
            className="today-button"
            variant={
              view === 'day' && isToday(currentDate) ? 'contained' : 'outlined'
            }
            href={`/v2/calendar/day/${format(
              startOfDay(Date.now()),
              DATE_PARAM_FORMAT
            )}`}
          >
            Today
          </Button>
          <Button
            key="week"
            className="week-button"
            variant={view === 'week' ? 'contained' : 'outlined'}
            href={`/v2/calendar/week/${safeFormat(
              currentDate,
              DATE_PARAM_FORMAT
            )}`}
          >
            Week
          </Button>
          <Button
            key="month"
            className="month-button"
            variant={view === 'month' ? 'contained' : 'outlined'}
            href={`/v2/calendar/month/${safeFormat(
              currentDate,
              DATE_PARAM_FORMAT
            )}`}
          >
            Month
          </Button>
        </ButtonsWrapper>
      </FilterWrapper>
    </Wrapper>
  )
}

export default Filterbar
